import {Component} from '@packages/systems/wfdl';
import {getPluginName, getName} from '@packages/systems/wfdl-Qualified';
import {weakMemo} from '@packages/utilities/memo';
// Direct-file import to skip the @packages/systems/component-slots barrel,
// which re-enters this package through user-components/utils mid-load.
// eslint-disable-next-line webflow/module-top-level-imports
import {getSlotDefaultComponentInstances} from '@packages/systems/component-slots/SlotDefaultUtils';
import {buildComponentInstancesGetter} from './buildComponentInstancesGetter';

export type ComponentNestingMaps = {
  transitive: ReadonlyMap<string, ReadonlySet<string>>;
  directChildren: ReadonlyMap<string, ReadonlySet<string>>;
  directParents: ReadonlyMap<string, ReadonlySet<string>>;
  childPlacementCounts: ReadonlyMap<string, ReadonlyMap<string, number>>;
  slotDefaultPlacementCounts: ReadonlyMap<string, ReadonlyMap<string, number>>;
  slotDefaultParents: ReadonlyMap<string, ReadonlySet<string>>;
};

const EMPTY_COMPONENT_NESTING_MAPS: ComponentNestingMaps = {
  transitive: new Map(),
  directChildren: new Map(),
  directParents: new Map(),
  childPlacementCounts: new Map(),
  slotDefaultPlacementCounts: new Map(),
  slotDefaultParents: new Map(),
};

function isSitePluginComponent(
  componentType: Parameters<typeof getPluginName>[0]
): boolean {
  return (
    getPluginName(componentType) === '__SitePlugin' &&
    getName(componentType) !== 'page'
  );
}

/**
 * Single traversal of all component render trees that produces:
 * - transitive: parentId -> Set of all transitively nested componentIds
 * - directChildren: parentId -> Set of direct child componentIds
 * - directParents: childId -> Set of all direct parent componentIds
 * - childPlacementCounts: parentId -> childId -> number of placements
 *
 * Only __SitePlugin user components are traversed. Library component roots
 * (those with an origin) are excluded because their definitions cannot be
 * focused into or edited, and their nested instances should not contribute
 * to instance counts or navigation chains.
 *
 * Cycle-safety: the maps produced here (directChildren / childPlacementCounts)
 * feed a downstream topological (Kahn's) propagation in getCount. Neither this
 * traversal nor that propagation defensively guards against cycles — component
 * definitions are guaranteed acyclic by validateNoCircularComponents, which
 * blocks circular nesting before it can be saved.
 *
 * Memoized on the components reference so repeated calls with the same
 * component map (e.g. from componentInstanceCountsSummary and the IX3
 * panel in the same render) return cached results.
 *
 * Note: this traverses component definitions, not page instances. Slot
 * overrides on specific instances are not reflected — a component reported
 * as nested may not be present in every instance if its slot was overridden.
 * TODO: Consider filtering at display time for slot-override accuracy.
 */
export const computeComponentNestingMaps = weakMemo(
  (components: Component.ComponentMap): ComponentNestingMaps => {
    if (!components) return EMPTY_COMPONENT_NESTING_MAPS;

    const directChildren = new Map<string, Set<string>>();
    const directParents = new Map<string, Set<string>>();
    const childPlacementCounts = new Map<string, Map<string, number>>();
    const slotDefaultPlacementCounts = new Map<string, Map<string, number>>();
    const slotDefaultParents = new Map<string, Set<string>>();

    const findInstances = buildComponentInstancesGetter(components);

    function getOrCreateSet(map: Map<string, Set<string>>, key: string) {
      let set = map.get(key);
      if (!set) {
        set = new Set();
        map.set(key, set);
      }
      return set;
    }

    components.forEach((component, componentType) => {
      if (!isSitePluginComponent(componentType)) return;
      if (Component.getOrigin(component)) return;
      const parentId = getName(componentType);
      if (typeof parentId !== 'string' || !parentId) return;
      const render = Component.getRender(component);
      const nestedInstances = findInstances(render);

      for (const instance of nestedInstances) {
        const childId = getName(instance.type);
        if (typeof childId !== 'string' || !childId) continue;
        getOrCreateSet(directChildren, parentId).add(childId);
        getOrCreateSet(directParents, childId).add(parentId);

        let parentCounts = childPlacementCounts.get(parentId);
        if (!parentCounts) {
          parentCounts = new Map();
          childPlacementCounts.set(parentId, parentCounts);
        }
        parentCounts.set(childId, (parentCounts.get(childId) ?? 0) + 1);
      }

      // Slot-default edges: a component authored in this component's slot
      // defaults. Same acyclic assumption as render nesting.
      for (const [, childType] of getSlotDefaultComponentInstances(component)) {
        const childId = getName(childType);
        if (typeof childId !== 'string' || !childId) continue;
        getOrCreateSet(slotDefaultParents, childId).add(parentId);

        let parentSlotCounts = slotDefaultPlacementCounts.get(parentId);
        if (!parentSlotCounts) {
          parentSlotCounts = new Map();
          slotDefaultPlacementCounts.set(parentId, parentSlotCounts);
        }
        parentSlotCounts.set(childId, (parentSlotCounts.get(childId) ?? 0) + 1);
      }
    });

    const transitive = new Map<string, Set<string>>();
    for (const [parentId] of directChildren) {
      const allDescendants = new Set<string>();
      const queue = [...(directChildren.get(parentId) ?? [])];
      let head = 0;
      while (head < queue.length) {
        const current = queue[head++]!;
        if (allDescendants.has(current)) continue;
        allDescendants.add(current);
        const grandchildren = directChildren.get(current);
        if (grandchildren) {
          for (const gc of grandchildren) {
            if (!allDescendants.has(gc)) queue.push(gc);
          }
        }
      }
      if (allDescendants.size > 0) {
        transitive.set(parentId, allDescendants);
      }
    }

    return {
      transitive,
      directChildren,
      directParents,
      childPlacementCounts,
      slotDefaultPlacementCounts,
      slotDefaultParents,
    };
  }
);
